.root {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--aura-space-2);
  width: var(--multi-select-width, 320px);
  max-width: var(--multi-select-max-width, none);
  font-family: var(--aura-font-sans);
}

.rootFullWidth {
  --multi-select-width: 100%;
  width: 100%;
}

.rootContained {
  width: min(var(--multi-select-width, 320px), 100%);
}

.label {
  font-size: 0.9rem;
  font-weight: var(--aura-font-weight-medium);
  color: var(--aura-color-global-text-secondary);
}

.container {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--aura-space-2);
  padding: var(--multi-select-padding-y, 0.5rem)
    var(--multi-select-padding-x, 0.75rem);
  border-radius: var(--aura-radius-xl);
  border: 1px solid
    color-mix(in srgb, var(--aura-color-global-border-soft) 80%, transparent);
  background: color-mix(
    in srgb,
    var(--aura-color-glass-surface) 80%,
    transparent
  );
  backdrop-filter: blur(var(--aura-glass-neutral-level2-backdrop-blur));
  -webkit-backdrop-filter: blur(var(--aura-glass-neutral-level2-backdrop-blur));
  transition:
    border var(--aura-motion-duration-fast) var(--aura-motion-easing-standard),
    box-shadow var(--aura-motion-duration-fast)
      var(--aura-motion-easing-standard);
  cursor: text;
}

.containerFocused {
  border-color: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 70%,
    transparent
  );
  box-shadow: var(
    --glass-elev-multiselect-focus,
    0 0 0 2px
      color-mix(in srgb, var(--aura-color-semantic-primary) 25%, transparent)
  );
}

.containerError {
  border-color: var(
    --glass-multiselect-error-border,
    color-mix(in srgb, var(--aura-color-semantic-error) 80%, transparent)
  );
  box-shadow: var(
    --glass-elev-multiselect-error,
    0 0 0 2px
      color-mix(in srgb, var(--aura-color-semantic-error) 18%, transparent)
  );
}

.containerDisabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sizeSmall {
  --multi-select-padding-y: 0.25rem;
  --multi-select-padding-x: 0.5rem;
  min-height: 36px;
}

.containerCompact {
  gap: var(--aura-space-1);
  border-radius: var(--aura-radius-lg);
}

.sizeMedium {
  --multi-select-padding-y: 0.5rem;
  --multi-select-padding-x: 0.75rem;
  min-height: 40px;
}

.sizeLarge {
  --multi-select-padding-y: 0.625rem;
  --multi-select-padding-x: 1rem;
  min-height: 48px;
}

.tokenList {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--aura-space-2);
  flex: 1;
  min-width: 0;
}

.placeholder {
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 70%,
    transparent
  );
  font-size: 0.95rem;
}

.token {
  display: inline-flex;
  align-items: center;
  gap: var(--aura-space-2);
  padding: 0.15rem 0.5rem;
  border-radius: var(--glass-radius-pill, 999px);
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 25%,
    transparent
  );
  border: 1px solid
    color-mix(in srgb, var(--aura-color-semantic-primary) 45%, transparent);
  color: var(--aura-color-global-text-primary);
  font-size: 0.85rem;
  line-height: 1;
}

.tokenDisabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tokenLabel {
  max-width: min(160px, 42vw);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.removeButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-primary) 70%,
    transparent
  );
  cursor: pointer;
  transition:
    background var(--aura-motion-duration-fast)
      var(--aura-motion-easing-standard),
    color var(--aura-motion-duration-fast) var(--aura-motion-easing-standard);
}

.removeButton:hover,
.removeButton:focus-visible {
  outline: none;
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 25%,
    transparent
  );
  color: var(--aura-color-global-text-primary);
}

.input {
  flex: 1;
  min-width: 120px;
  border: none;
  background: transparent;
  color: var(--aura-color-global-text-primary);
  font-size: 0.95rem;
  padding: 0;
  line-height: 1.4;
}

.input:focus {
  outline: none;
}

.input::placeholder {
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 55%,
    transparent
  );
}

.clearButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 65%,
    transparent
  );
  cursor: pointer;
  padding: 0.25rem;
  border-radius: var(--aura-radius-sm);
  transition:
    background var(--aura-motion-duration-fast)
      var(--aura-motion-easing-standard),
    color var(--aura-motion-duration-fast) var(--aura-motion-easing-standard);
}

.clearButton:hover,
.clearButton:focus-visible {
  outline: none;
  color: var(--aura-color-global-text-primary);
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 20%,
    transparent
  );
}

.dropdown {
  position: absolute;
  left: 0;
  right: 0;
  margin-top: var(--aura-space-2);
  z-index: 20;
  border-radius: var(--aura-radius-xl);
  background: color-mix(
    in srgb,
    var(--aura-color-glass-surface) 92%,
    transparent
  );
  border: 1px solid
    color-mix(in srgb, var(--aura-color-global-border-soft) 80%, transparent);
  box-shadow: var(
    --glass-elev-multiselect-dropdown,
    0 18px 48px color-mix(in srgb, var(--glass-gray-900) 32%, transparent)
  );
  max-height: var(--multi-select-dropdown-height, 280px);
  overflow: hidden;
  display: none;
}

.dropdownVisible {
  display: block;
}

.dropdownAbove {
  bottom: calc(100% + var(--aura-space-2));
  top: auto;
  margin-top: 0;
}

.optionList {
  list-style: none;
  margin: 0;
  padding: var(--aura-space-2) 0;
  max-height: inherit;
  overflow-y: auto;
  scrollbar-width: thin;
}

.optionList::-webkit-scrollbar {
  width: 6px;
}

.optionList::-webkit-scrollbar-thumb {
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 45%,
    transparent
  );
  border-radius: var(--aura-radius-md);
}

.option {
  display: flex;
  align-items: center;
  gap: var(--aura-space-2);
  padding: 0.65rem 1rem;
  cursor: pointer;
  color: var(--aura-color-global-text-primary);
  transition:
    background var(--aura-motion-duration-fast)
      var(--aura-motion-easing-standard),
    color var(--aura-motion-duration-fast) var(--aura-motion-easing-standard);
}

.option:hover {
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 20%,
    transparent
  );
}

.optionSelected {
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 35%,
    transparent
  );
}

.optionFocused {
  outline: none;
  background: color-mix(
    in srgb,
    var(--aura-color-semantic-primary) 25%,
    transparent
  );
}

.optionDisabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.groupHeader {
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 65%,
    transparent
  );
}

.helperText {
  font-size: 0.8rem;
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 75%,
    transparent
  );
}

.errorText {
  font-size: 0.8rem;
  color: var(
    --glass-multiselect-error-text,
    color-mix(in srgb, var(--aura-color-semantic-error) 90%, transparent)
  );
}

.noOptions {
  padding: 0.75rem 1rem;
  text-align: center;
  font-style: italic;
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 70%,
    transparent
  );
}

.loading {
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: var(--aura-space-2);
  color: var(--aura-color-global-text-secondary);
}

.spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    color-mix(in srgb, var(--aura-color-semantic-primary) 35%, transparent);
  border-top-color: var(--aura-color-semantic-primary);
  animation: spin var(--aura-motion-duration-slow) linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
